// The onGuard portal permission table (ENTRAID-358, spec 2026-11-03-permission-bitmask-table-design.md): one table,
// rows are resources, columns are principals (the six tenant roles plus the operator), cells are sets of actions
// compiled to an integer bitmask. Shared by the backend (lib/permissions-table.mjs re-exports it) and the SPA, which
// uses it for display only, never as a security boundary.
//
// Pure data and pure functions: no I/O, no env reads, no node: imports (the SPA bundles this file with Vite).
// Deny by default: a resource or principal absent from the table is mask 0. Compiled objects are frozen with null
// prototypes; read them with Object.hasOwn only.
import { TENANT_ROLES, TENANT_ROLE_LABELS, ROLE_PREFIX, ADMIN_ROLE, DEVELOPER_ROLE, QLIK_CREDENTIALS_ROLE, AUDITOR_ROLE, VIEWER_ROLE, ACCESS_REVIEWER_ROLE } from './solution.mjs';

// Action vocabulary: the same name has the same bit on every resource, so the UI and audit readers decode a mask
// without a per-resource legend. APPEND-ONLY: a bit is never renumbered or reused (audit lines persist masks), pinned
// by portal/backend/tests/fixtures/permission-bits.snapshot.json. At most 31 bits (JavaScript bitwise is 32-bit signed).
export const ACTIONS = Object.freeze({ read: 1, create: 2, update: 4, delete: 8, execute: 16, reveal: 32, export: 64, attest: 128 });

// The operator column. Same string as portal/backend/lib/role-matrix.mjs OPERATOR (pinned by the backend test).
export const OPERATOR = 'operator';

// Every principal a cell may name: the tenant roles of shared/solution.mjs plus the operator.
export const PRINCIPALS = Object.freeze([...TENANT_ROLES, OPERATOR]);

// Principal conditions switch a whole column on or off; the value names the boolean fact that must be true, or lists
// several, all of which must be true, checked in order.
//   - reviewer counts only while REVIEWER_ROLE_REGISTERED is 'true'.
//   - viewer counts only while VIEWER_ROLE_REGISTERED is 'true' (spec 6A C1, Q1).
//   - auditor counts only while AUDITOR_ROLE_REGISTERED is 'true' (spec 6A C2, Q1) AND with a live grant row in
//     onguard-compliance-auditors. The flag comes first: while it is off no grant row is read (lib/permissions.mjs).
export const PRINCIPAL_CONDITIONS = Object.freeze(Object.assign(Object.create(null), {
  [ACCESS_REVIEWER_ROLE]: 'reviewerRegistered',
  [VIEWER_ROLE]: 'viewerRegistered',
  [AUDITOR_ROLE]: Object.freeze(['auditorRegistered', 'auditorGrantLive']),
}));

/** The condition names of a principal's column, in order (none for an unconditioned principal). */
export const principalConditionNames = (principal) => {
  if (!Object.hasOwn(PRINCIPAL_CONDITIONS, principal)) return [];
  const v = PRINCIPAL_CONDITIONS[principal];
  return Array.isArray(v) ? v : [v];
};

// Cell conditions (spec section 3), each a property of one compiled cell:
//   - staffOwnUserOnly: true      the cell counts for a staff caller (operator or any IPC Global SSO sign-in) only on
//                                 customer zero (the customer's own user); non-staff callers are unaffected.
//                       'operator' the same rule, but only for an operator caller; a non-operator member (staff or not)
//                                 holding the role counts on any customer (watch.notice, today's watchRecipientOrAdmin).
//                       false     no own-user rule (default).
//   - operatorTier:     for an operator caller holding this column on a Watch resource, the cell counts only when the
//                       customer's tier allows operator Watch content (operatorAdminWatchAllowedForTier).
//   - operatorViaIdp:   an operator whose token lacks this role may gain this column through the central IdP admin
//                       membership lookup (lib/admin-membership.mjs). Only where today's code does that.
export const CELL_FLAGS = Object.freeze(['staffOwnUserOnly', 'operatorTier', 'operatorViaIdp']);

// Registration flags a cell may depend on (`flag`): the cell counts only while that flag is on. During the parity
// migration only people.grant.viewer and people.grant.auditor carry one (spec section 3, D1).
export const REGISTRATION_FLAGS = Object.freeze(['reviewer', 'viewer', 'auditor']);

const STAFF_OWN_USER_ONLY_VALUES = Object.freeze([false, true, 'operator']);
const CELL_KEYS = Object.freeze(['actions', ...CELL_FLAGS, 'flag']);
const MAX_BITS = 31;

const A = ADMIN_ROLE;
const O = OPERATOR;
const Q = QLIK_CREDENTIALS_ROLE;
const AU = AUDITOR_ROLE;
const R = ACCESS_REVIEWER_ROLE;
const D = DEVELOPER_ROLE;
const V = VIEWER_ROLE;

// Every principal from the highest privilege to the lowest: the admin, then the operator, then the other tenant roles.
// A copy of portal/backend/lib/role-matrix.mjs ACCESS_ORDER (this file cannot import backend code); the backend test
// tests/lib/permissions-pure.test.mjs pins the two equal.
export const ACCESS_ORDER = Object.freeze([A, O, Q, AU, R, D, V]);

// Authored cells: an array of action names, or { actions, staffOwnUserOnly?, operatorTier?, operatorViaIdp?, flag? }.
const viaIdp = (...actions) => ({ actions, operatorViaIdp: true });
const ownUser = (...actions) => ({ actions, staffOwnUserOnly: true });
const operatorOwnUser = (...actions) => ({ actions, staffOwnUserOnly: 'operator' });
const CRUD = ['read', 'create', 'update', 'delete'];

// People offers for the remaining grantable roles (admin, developer, qlik-credentials): open to A and O. The resource
// name is people.grant.<role without the solution prefix>, the role name taken from shared/solution.mjs.
const grantRow = (role) => ({
  resource: `people.grant.${role.slice(ROLE_PREFIX.length)}`,
  label: `People: give the ${TENANT_ROLE_LABELS[role]} role`,
  section: 'People',
  cells: { [A]: ['create'], [O]: ['create'] },
});

// The authored table: spec 2026-11-03 section 4.2, in its order. Fleet resources have no customer: only the operator
// column applies. Narrowed views are separate resources (runs.summary, licenses.names), never extra actions.
export const PERMISSIONS = Object.freeze([
  { resource: 'fleet.customers', label: 'Fleet: customers', section: 'Fleet', cells: { [O]: ['read', 'create'] } },
  { resource: 'fleet.code', label: 'Fleet: push collector code', section: 'Fleet', cells: { [O]: ['execute'] } },
  { resource: 'fleet.accessRequests', label: 'Fleet: access requests', section: 'Fleet', cells: { [O]: ['read', 'delete'] } },
  { resource: 'fleet.watch', label: 'Fleet: Watch health', section: 'Fleet', cells: { [O]: ['read'] } },

  { resource: 'customer.summary', label: 'Customer: summary', section: 'Customer', cells: { [A]: ['read'], [O]: ['read'], [V]: ['read'] } },
  { resource: 'customer', label: 'Customer: full record', section: 'Customer', cells: { [A]: ['read'], [O]: ['read'] } },
  { resource: 'customer.platform', label: 'Customer: platform fields', section: 'Customer', cells: { [O]: ['read', 'update'] } },
  { resource: 'customer.accountNames', label: 'Customer: account names', section: 'Customer', cells: { [A]: ['update'], [O]: ['update'] } },
  { resource: 'customer.lifecycle', label: 'Customer: lifecycle', section: 'Customer', cells: { [O]: ['execute', 'delete'] } },
  { resource: 'customer.accessRequests', label: 'Customer: access requests', section: 'Customer', cells: { [O]: ['read'] } },

  { resource: 'setup', label: 'Setup and deployment', section: 'Setup', cells: { [A]: ['read', 'execute'], [O]: ['read', 'execute'] } },
  { resource: 'config', label: 'Configuration', section: 'Setup', cells: { [A]: ['update'], [O]: ['update'] } },

  { resource: 'runs.summary', label: 'Runs: summary', section: 'Runs', cells: { [A]: ['read'], [O]: ['read'], [V]: ['read'] } },
  { resource: 'runs', label: 'Runs', section: 'Runs', cells: { [A]: ['read', 'execute'], [O]: ['read', 'execute'] } },
  { resource: 'changes', label: 'Changes', section: 'Runs', cells: { [A]: ['read'], [O]: ['read'], [V]: ['read'] } },

  { resource: 'dataReference', label: 'Data reference', section: 'Data', cells: { [A]: ['read'], [O]: ['read'], [D]: ['read'] } },
  { resource: 'footprint', label: 'Implementation footprint', section: 'Data', cells: { [A]: ['read'], [O]: ['read'], [AU]: ['read'], [D]: ['read'] } },
  // The tenant audience (the consent admin's UPN). An operator caller reads it only as the customer's own user (admin or
  // developer) on customer zero, else the evidence audience; the operator column itself never grants it. Spec 4.2 had
  // `read (o)` on the operator column; its note and the golden say the rule sits on the A and D cells, for operator
  // callers only (ENTRAID-358 PR 2 reconciliation).
  { resource: 'footprint.consentAdmin', label: 'Implementation footprint: consent audience', section: 'Data', cells: { [A]: operatorOwnUser('read'), [D]: operatorOwnUser('read') } },

  { resource: 'reports', label: 'Reports: list', section: 'Compliance', cells: { [A]: viaIdp('read'), [O]: ['read'], [AU]: ['read'], [D]: ['read'] } },
  { resource: 'report.compliance-readiness', label: 'Report: compliance readiness', section: 'Compliance', cells: { [A]: viaIdp('read', 'create'), [AU]: ['read'] } },
  { resource: 'report.security-posture', label: 'Report: security posture', section: 'Compliance', cells: { [A]: viaIdp('read', 'create'), [AU]: ['read'] } },
  { resource: 'report.access-review', label: 'Report: access review', section: 'Compliance', cells: { [A]: viaIdp('read'), [AU]: ['read'] } },
  { resource: 'report.implementation-footprint', label: 'Report: implementation footprint', section: 'Compliance', cells: { [A]: viaIdp('read', 'create'), [O]: ['read', 'create'], [AU]: ['read', 'create'], [D]: ['read', 'create'] } },
  { resource: 'report.licenses-usage', label: 'Report: licenses usage', section: 'Compliance', cells: { [A]: viaIdp('read', 'create'), [O]: ['read', 'create'], [AU]: ['read'] } },
  { resource: 'compliance.controls', label: 'Compliance: controls', section: 'Compliance', cells: { [A]: viaIdp('read'), [AU]: ['read'] } },
  { resource: 'compliance.attestations', label: 'Compliance: attestations', section: 'Compliance', cells: { [A]: viaIdp('read', 'create'), [AU]: ['read'] } },
  { resource: 'compliance.schedule', label: 'Compliance: schedule', section: 'Compliance', cells: { [A]: viaIdp('read', 'update') } },
  { resource: 'compliance.audit', label: 'Compliance: audit and auditors', section: 'Compliance', cells: { [A]: viaIdp('read'), [O]: ['read'] } },

  { resource: 'licenses', label: 'Licenses', section: 'Licenses', cells: { [A]: ['read', 'update'], [O]: ['read', 'update'], [V]: ['read'] } },
  { resource: 'licenses.names', label: 'Licenses: user names', section: 'Licenses', cells: { [A]: ownUser('read') } },
  // The CSV export lists the people behind the findings, so it follows the names rule (routes/customer-licenses.mjs
  // handleExport refuses unless seesNames): the customer's own admin only. Spec 4.2 had export on licenses for A and O;
  // the golden refuses the operator and a staff admin off customer zero (ENTRAID-358 PR 2 reconciliation).
  { resource: 'licenses.export', label: 'Licenses: export the people', section: 'Licenses', cells: { [A]: ownUser('export') } },

  { resource: 'accessReviews', label: 'Access Reviews', section: 'Access Reviews', cells: { [A]: ['read'], [O]: ['read'], [V]: ['read'] } },
  { resource: 'accessReviews.names', label: 'Access Reviews: user names', section: 'Access Reviews', cells: { [A]: ownUser('read') } },
  { resource: 'accessReviews.campaigns', label: 'Access Reviews: campaigns', section: 'Access Reviews', cells: { [A]: ['create', 'update', 'execute'], [O]: ['create', 'update', 'execute'] } },
  { resource: 'accessReviews.notes', label: 'Access Reviews: notes', section: 'Access Reviews', cells: { [A]: ['create', 'update', 'delete'], [O]: ['create', 'update', 'delete'] } },
  { resource: 'accessReviews.close', label: 'Access Reviews: close a campaign', section: 'Access Reviews', cells: { [A]: ownUser('attest') } },
  { resource: 'accessReviews.mine', label: 'Access Reviews: your reviews', section: 'Access Reviews', cells: { [A]: ownUser('read', 'attest'), [R]: ownUser('read', 'attest') } },

  { resource: 'companySettings', label: 'Company settings', section: 'Company settings', cells: { [A]: viaIdp('read', 'update') } },
  { resource: 'companySettings.state', label: 'Company settings: state', section: 'Company settings', cells: { [A]: viaIdp('read'), [O]: ['read'] } },
  { resource: 'companySettings.integrations', label: 'Company settings: integrations', section: 'Company settings', cells: { [A]: viaIdp('create', 'update', 'delete', 'execute') } },
  { resource: 'branding.logo', label: 'Branding: logo', section: 'Company settings', cells: { [A]: ['read'], [O]: ['read'], [Q]: ['read'], [AU]: ['read'], [R]: ['read'], [D]: ['read'], [V]: ['read'] } },
  { resource: 'sendingDomain', label: 'Company settings: sending domain', section: 'Company settings', cells: { [A]: viaIdp('read', 'create', 'update', 'delete', 'execute'), [O]: ['read'] } },

  { resource: 'qlik', label: 'Qlik Cloud', section: 'Qlik Cloud', cells: { [A]: ['read'], [O]: ['read', 'update'], [Q]: ['read'] } },
  { resource: 'qlik.secret', label: 'Qlik Cloud: reveal credentials', section: 'Qlik Cloud', cells: { [A]: ['reveal'], [O]: ['reveal'], [Q]: ['reveal'] } },
  { resource: 'qlik.quote', label: 'Qlik Cloud: request a quote', section: 'Qlik Cloud', cells: { [A]: viaIdp('create') } },

  { resource: 'notifications', label: 'Notifications', section: 'Notifications', cells: { [A]: [...CRUD, 'execute'], [O]: [...CRUD, 'execute'] } },
  // C5 (spec 6A, D8): only the customer admin turns the master switch on or off. "Customer admin" is exactly what the Live
  // watch reconcile behind it counts as the admin (the `watch` row's admin cell): the admin role in the token, or an
  // operator who is the customer's admin through the central IdP, an operator only on a tier that allows operator Watch.
  { resource: 'notifications.master', label: 'Notifications: master switch', section: 'Notifications', cells: { [A]: { actions: ['update'], operatorViaIdp: true, operatorTier: true } } },

  // The Watch page's entry: the customer's admin (who then reads content through `watch`) and the operator (health only).
  // Spec 4.2 had the operator only; the golden admits a non-operator admin (ENTRAID-358 PR 2 reconciliation).
  { resource: 'watch.state', label: 'Watch: state', section: 'Watch', cells: { [A]: ['read'], [O]: ['read'] } },
  { resource: 'watch', label: 'Watch', section: 'Watch', cells: { [A]: { actions: CRUD, operatorViaIdp: true, operatorTier: true } } },
  { resource: 'watch.killSwitch', label: 'Watch: kill switch', section: 'Watch', cells: { [O]: ['execute'] } },
  {
    resource: 'watch.notice',
    label: 'Watch: notices',
    section: 'Watch',
    cells: {
      [A]: { actions: ['read', 'update', 'attest'], operatorViaIdp: true, operatorTier: true },
      [Q]: { actions: ['read', 'update', 'attest'], staffOwnUserOnly: 'operator' },
      [D]: { actions: ['read', 'update', 'attest'], staffOwnUserOnly: 'operator' },
    },
  },

  { resource: 'people', label: 'People', section: 'People', cells: { [A]: CRUD, [O]: CRUD } },
  // An operator gives the auditor role only as the customer's admin (token or IdP). Revoking stays people:delete.
  { resource: 'people.grant.auditor', label: `People: give the ${TENANT_ROLE_LABELS[AU]} role`, section: 'People', cells: { [A]: { actions: ['create'], operatorViaIdp: true, flag: 'auditor' } } },
  { resource: 'people.grant.viewer', label: `People: give the ${TENANT_ROLE_LABELS[V]} role`, section: 'People', cells: { [A]: { actions: ['create'], flag: 'viewer' }, [O]: { actions: ['create'], flag: 'viewer' } } },
  grantRow(A),
  grantRow(D),
  grantRow(Q),
].map((r) => Object.freeze(r)));

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const RESOURCE_NAME = /^[a-z][A-Za-z0-9]*(?:[.-][A-Za-z0-9]+)*$/;

function checkVocabulary(actions) {
  if (!isPlainObject(actions)) throw new Error('permissions: the action vocabulary must be an object');
  const names = Object.keys(actions);
  if (names.length > MAX_BITS) throw new Error(`permissions: at most ${MAX_BITS} actions, got ${names.length}`);
  const seen = new Map();
  for (const name of names) {
    const bit = actions[name];
    if (!Number.isInteger(bit) || bit <= 0 || bit > 2 ** (MAX_BITS - 1) || (bit & (bit - 1)) !== 0) {
      throw new Error(`permissions: action "${name}" must be a single bit below 2^${MAX_BITS}, got ${bit}`);
    }
    if (seen.has(bit)) throw new Error(`permissions: action "${name}" reuses bit ${bit} of "${seen.get(bit)}"`);
    seen.set(bit, name);
  }
}

function compileCell(resource, principal, authored, actions) {
  const where = `resource "${resource}" principal "${principal}"`;
  const cell = Array.isArray(authored) ? { actions: authored } : authored;
  if (!isPlainObject(cell)) throw new Error(`permissions: ${where}: a cell is an action list or an object`);
  for (const key of Object.keys(cell)) {
    if (!CELL_KEYS.includes(key)) throw new Error(`permissions: ${where}: unknown cell key "${key}"`);
  }
  if (!Array.isArray(cell.actions) || cell.actions.length === 0) throw new Error(`permissions: ${where}: no actions`);
  let mask = 0;
  for (const name of cell.actions) {
    if (typeof name !== 'string' || !Object.hasOwn(actions, name)) throw new Error(`permissions: ${where}: unknown action "${name}"`);
    if ((mask & actions[name]) !== 0) throw new Error(`permissions: ${where}: repeats action "${name}"`);
    mask |= actions[name];
  }
  const staffOwnUserOnly = Object.hasOwn(cell, 'staffOwnUserOnly') ? cell.staffOwnUserOnly : false;
  if (!STAFF_OWN_USER_ONLY_VALUES.includes(staffOwnUserOnly)) throw new Error(`permissions: ${where}: staffOwnUserOnly must be true, false or 'operator'`);
  for (const key of ['operatorTier', 'operatorViaIdp']) {
    if (Object.hasOwn(cell, key) && typeof cell[key] !== 'boolean') throw new Error(`permissions: ${where}: ${key} must be a boolean`);
  }
  const compiled = Object.assign(Object.create(null), {
    mask,
    staffOwnUserOnly,
    operatorTier: cell.operatorTier === true,
    operatorViaIdp: cell.operatorViaIdp === true,
  });
  if (Object.hasOwn(cell, 'flag')) {
    if (!REGISTRATION_FLAGS.includes(cell.flag)) throw new Error(`permissions: ${where}: unknown flag "${cell.flag}"`);
    compiled.flag = cell.flag;
  }
  return Object.freeze(compiled);
}

/**
 * Compile an authored table (array of { resource, label, section, cells }) into frozen null-prototype masks:
 * masks[resource][principal] = { mask, staffOwnUserOnly, operatorTier, operatorViaIdp, flag? }.
 * Throws on an unknown action or principal, a duplicated or unsafe resource name, a missing label or section, a
 * malformed cell, or an action vocabulary over 31 bits. `hash` (string => hex string) is optional because this file
 * has no crypto: the backend passes node:crypto SHA-256 and gets `version`; without it `version` is null.
 */
export function compileTable(authored, { actions = ACTIONS, principals = PRINCIPALS, hash } = {}) {
  checkVocabulary(actions);
  if (!Array.isArray(authored)) throw new Error('permissions: the table must be an array of rows');
  const masks = Object.create(null);
  for (const row of authored) {
    if (!isPlainObject(row)) throw new Error('permissions: a row must be an object');
    const { resource, label, section, cells } = row;
    if (typeof resource !== 'string' || !RESOURCE_NAME.test(resource) || resource in Object.prototype) {
      throw new Error(`permissions: bad resource name "${resource}"`);
    }
    if (Object.hasOwn(masks, resource)) throw new Error(`permissions: duplicated resource "${resource}"`);
    if (typeof label !== 'string' || !label.trim()) throw new Error(`permissions: resource "${resource}" has no label`);
    if (typeof section !== 'string' || !section.trim()) throw new Error(`permissions: resource "${resource}" has no section`);
    if (!isPlainObject(cells) || Object.keys(cells).length === 0) throw new Error(`permissions: resource "${resource}" has no cells`);
    const compiled = Object.create(null);
    for (const principal of Object.keys(cells)) {
      if (!principals.includes(principal)) throw new Error(`permissions: resource "${resource}": unknown principal "${principal}"`);
      compiled[principal] = compileCell(resource, principal, cells[principal], actions);
    }
    masks[resource] = Object.freeze(compiled);
  }
  Object.freeze(masks);
  const canonical = canonicalTableJson(masks, actions);
  return { masks, canonical, version: typeof hash === 'function' ? hash(canonical).slice(0, 12) : null };
}

const sortedKeys = (o) => Object.keys(o).sort();

/**
 * Canonical JSON of a compiled table: keys sorted at every level, labels and sections left out (display only). It holds
 * everything that decides an answer, so TABLE_VERSION changes whenever a decision can: the action bits, the compiled
 * cells (masks and cell conditions), the principal conditions (which fact switches a whole column, ENTRAID-358 C1) and
 * the access order (who is the granting principal, and the order a refusal names them in).
 */
export function canonicalTableJson(masks, actions = ACTIONS, { principalConditions = PRINCIPAL_CONDITIONS, accessOrder = ACCESS_ORDER } = {}) {
  const out = { accessOrder: [...accessOrder], actions: {}, masks: {}, principalConditions: {} };
  for (const name of sortedKeys(actions)) out.actions[name] = actions[name];
  for (const principal of sortedKeys(principalConditions)) out.principalConditions[principal] = principalConditions[principal];
  for (const resource of sortedKeys(masks)) {
    out.masks[resource] = {};
    for (const principal of sortedKeys(masks[resource])) {
      const cell = masks[resource][principal];
      out.masks[resource][principal] = {};
      for (const key of sortedKeys(cell)) out.masks[resource][principal][key] = cell[key];
    }
  }
  return JSON.stringify(out);
}

/** The compiled table. masks[resource][principal]; absent means mask 0 (deny). */
export const MASKS = compileTable(PERMISSIONS).masks;

// First 12 hex characters of SHA-256 of canonicalTableJson(MASKS) (with the principal conditions and the access order
// since C1). Precomputed because the SPA has no node:crypto;
// portal/backend/tests/lib/permissions-table.test.mjs recomputes it and fails CI when it is stale. After any table
// change, regenerate from the repo root with:
//   node -e "import('./portal/backend/lib/permissions-table.mjs').then((m) => console.log(m.computeTableVersion()))"
export const TABLE_VERSION = 'd5f70b0ee2df';

// --- Pure mask functions (spec 5.1) -------------------------------------------------------------------------------------
// No I/O, no env: every fact a decision needs arrives in `facts`, each field fail closed (only a literal true counts).


/** The boolean fields of `facts` besides `flags` (spec 5.1). */
export const FACT_FIELDS = Object.freeze(['staff', 'operator', 'customerZero', 'watchTierAllows', 'auditorGrantLive', 'idpAdmin']);

// The facts that only RESTRICT (they switch on the staffOwnUserOnly and operatorTier conditions): a missing or
// non-boolean value reads as true, so a caller that forgets them can never gain bits (security review of PR 503).
const RESTRICTIVE_FACTS = Object.freeze(['staff', 'operator']);

/**
 * `facts` with every field present and boolean, fail closed: `staff` and `operator` keep a literal boolean and read as
 * true otherwise (restrictive); every other field (they grant) counts only as a literal `true`.
 */
export function normalizeFacts(facts) {
  const f = facts !== null && typeof facts === 'object' ? facts : {};
  const flags = f.flags !== null && typeof f.flags === 'object' ? f.flags : {};
  const out = {};
  for (const k of FACT_FIELDS) {
    const v = Object.hasOwn(f, k) ? f[k] : undefined;
    out[k] = RESTRICTIVE_FACTS.includes(k) ? v !== false : v === true;
  }
  out.flags = {};
  for (const k of REGISTRATION_FLAGS) out.flags[k] = Object.hasOwn(flags, k) && flags[k] === true;
  return out;
}

// The one place a principal condition name (PRINCIPAL_CONDITIONS) reads its fact.
const PRINCIPAL_CONDITION_FACT = Object.freeze(Object.assign(Object.create(null), {
  reviewerRegistered: (f) => f.flags.reviewer,
  viewerRegistered: (f) => f.flags.viewer,
  auditorRegistered: (f) => f.flags.auditor,
  auditorGrantLive: (f) => f.auditorGrantLive,
}));

const rowOf = (resource) => (typeof resource === 'string' && Object.hasOwn(MASKS, resource) ? MASKS[resource] : null);
const heldSet = (principals) => new Set(Array.isArray(principals) ? principals.filter((p) => typeof p === 'string' && PRINCIPALS.includes(p)) : []);

const NONE = Object.freeze(new Set());

/**
 * Whether `principal`'s cell on `row` counts for this caller. `held` is who the caller is; `excluded` names columns
 * that never count for it (customerRoleMask). For the restrictions an operator caller is the operator principal held
 * OR facts.operator (missing reads as true), and a staff caller is an operator caller or facts.staff. The IdP lift of
 * the admin column (operatorViaIdp) is a GRANT, so it needs the operator principal itself, never a fact alone.
 */
function cellCounts(row, principal, held, f, excluded = NONE) {
  if (!Object.hasOwn(row, principal) || excluded.has(principal)) return false;
  const cell = row[principal];
  const operatorCaller = held.has(O) || f.operator;
  const staffCaller = operatorCaller || f.staff;
  const viaIdp = cell.operatorViaIdp && principal === A && held.has(O) && f.idpAdmin;
  if (!held.has(principal) && !viaIdp) return false;
  if (!principalConditionNames(principal).every((name) => PRINCIPAL_CONDITION_FACT[name](f))) return false;
  if (Object.hasOwn(cell, 'flag') && !f.flags[cell.flag]) return false;
  if (cell.staffOwnUserOnly === true && staffCaller && !f.customerZero) return false;
  if (cell.staffOwnUserOnly === 'operator' && operatorCaller && !f.customerZero) return false;
  if (cell.operatorTier && operatorCaller && !f.watchTierAllows) return false;
  return true;
}

/**
 * The caller's mask on a resource: the OR of the cells of every principal it holds there, after the principal and cell
 * conditions. 0 for an unknown resource; unknown principals are ignored. `principals` are the ones held on THIS
 * customer only (the caller of customer A holds nothing on customer B but the operator).
 */
export function effectiveMask(resource, principals, facts) {
  const row = rowOf(resource);
  if (!row) return 0;
  const held = heldSet(principals);
  const f = normalizeFacts(facts);
  let mask = 0;
  for (const p of ACCESS_ORDER) if (cellCounts(row, p, held, f)) mask |= row[p].mask;
  return mask;
}

/** Per action of the effective mask, the highest principal in ACCESS_ORDER whose counted cell grants it (audit `grants`). */
export function grantsPerBit(resource, principals, facts) {
  return grantsOf(resource, principals, facts, () => NONE);
}

// The columns customerRoleMask leaves out (spec D9): the operator column, and an operator caller's own developer role.
const customerRoleExcluded = (held, f) => new Set([O, ...(held.has(O) || f.operator ? [D] : [])]);

/**
 * grantsPerBit of the customer roles only (spec D9, the same columns as customerRoleMask): per action, the highest
 * customer role whose counted cell grants it. The operator column never appears.
 */
export function customerRoleGrantsPerBit(resource, principals, facts) {
  return grantsOf(resource, principals, facts, customerRoleExcluded);
}

function grantsOf(resource, principals, facts, excludedOf) {
  const out = Object.create(null);
  const row = rowOf(resource);
  if (!row) return out;
  const held = heldSet(principals);
  const f = normalizeFacts(facts);
  const excluded = excludedOf(held, f);
  for (const p of ACCESS_ORDER) {
    if (!cellCounts(row, p, held, f, excluded)) continue;
    for (const [name, bit] of Object.entries(ACTIONS)) if ((row[p].mask & bit) !== 0 && !Object.hasOwn(out, name)) out[name] = p;
  }
  return out;
}

/**
 * The mask the caller's CUSTOMER ROLES grant on a resource (spec D9, reconciled against the golden in PR 2): the
 * operator column never counts, and for an operator caller its own developer role never counts either (today's
 * compliance gate reads no customer record for it, customer zero included). The admin column still counts through the
 * IdP (operatorViaIdp) for an operator. Compliance uses it to choose between the content view (a customer role reads
 * it) and the operator health view (only the operator column does): report rows in the list, report.requestedBy.
 */
export function customerRoleMask(resource, principals, facts) {
  const row = rowOf(resource);
  if (!row) return 0;
  const held = heldSet(principals);
  const f = normalizeFacts(facts);
  const excluded = customerRoleExcluded(held, f);
  let mask = 0;
  for (const p of ACCESS_ORDER) if (cellCounts(row, p, held, f, excluded)) mask |= row[p].mask;
  return mask;
}

const VOCABULARY_MASK = Object.values(ACTIONS).reduce((m, b) => m | b, 0);

/**
 * The principals a 403 names (spec 5.3): every principal whose cell holds all of `required`, in ACCESS_ORDER, the
 * reviewer, the viewer (C1) and the auditor (C2) only while registered. Cell conditions never hide a principal (the auditor is named without a live grant).
 */
export function grantingPrincipals(resource, required, facts) {
  const row = rowOf(resource);
  if (!row || !Number.isInteger(required) || required <= 0 || (required & ~VOCABULARY_MASK) !== 0) return [];
  const f = normalizeFacts(facts);
  return ACCESS_ORDER.filter((p) => Object.hasOwn(row, p) && (row[p].mask & required) === required && (p !== R || f.flags.reviewer) && (p !== V || f.flags.viewer) && (p !== AU || f.flags.auditor));
}

/** The action names of a mask, in vocabulary order; unknown bits are ignored. */
export function actionsOf(mask) {
  if (!Number.isInteger(mask)) return [];
  return Object.keys(ACTIONS).filter((name) => (mask & ACTIONS[name]) !== 0);
}

// Plain words for the on-screen matrix (spec 5.6).
const CONDITION_WORDS = Object.freeze({
  reviewerRegistered: 'only while the reviewer role is registered',
  viewerRegistered: 'only while the viewer role is registered',
  auditorRegistered: 'only while the auditor role is registered',
  auditorGrantLive: 'only while the auditor access is live',
  staffOwnUserOnly: "only for staff on IPC Global's own tenant",
  staffOwnUserOnlyOperator: "for IPC Global operators, only on IPC Global's own tenant",
  operatorTier: 'for IPC Global operators, only on a tier that allows it',
  operatorViaIdp: 'IPC Global operators too, when they are the customer\'s administrator',
  flag: (flag) => `only while the ${flag} role is registered`,
});

/**
 * The role matrix for display (spec 5.6): resources grouped by section in table order, one column per principal in
 * ACCESS_ORDER (the reviewer, the viewer and the auditor only while registered), each cell its actions and its conditions in plain words.
 */
export function matrixForDisplay(facts) {
  const f = normalizeFacts(facts);
  const principals = ACCESS_ORDER.filter((p) => (p !== R || f.flags.reviewer) && (p !== V || f.flags.viewer) && (p !== AU || f.flags.auditor));
  const sections = [];
  for (const { resource, label, section } of PERMISSIONS) {
    let s = sections[sections.length - 1];
    if (!s || s.label !== section) { s = { label: section, rows: [] }; sections.push(s); }
    const row = MASKS[resource];
    const cells = {};
    for (const p of principals) {
      if (!Object.hasOwn(row, p)) { cells[p] = { actions: [], conditions: [] }; continue; }
      const cell = row[p];
      const conditions = [];
      for (const name of principalConditionNames(p)) conditions.push(CONDITION_WORDS[name]);
      if (cell.staffOwnUserOnly === true) conditions.push(CONDITION_WORDS.staffOwnUserOnly);
      if (cell.staffOwnUserOnly === 'operator') conditions.push(CONDITION_WORDS.staffOwnUserOnlyOperator);
      if (cell.operatorViaIdp) conditions.push(CONDITION_WORDS.operatorViaIdp);
      if (cell.operatorTier) conditions.push(CONDITION_WORDS.operatorTier);
      if (Object.hasOwn(cell, 'flag')) conditions.push(CONDITION_WORDS.flag(cell.flag));
      cells[p] = { actions: actionsOf(cell.mask), conditions };
    }
    s.rows.push({ resource, label, cells });
  }
  return { principals, sections };
}
